import { useQuery } from '@tanstack/react-query';
import type {
  ExportDestinationDTO, StorageAgentDTO, StorageLocationSummary, StoragePolicyDTO, TranscoderInfo, TransferDTO, WatchFolderDTO, WatchFolderItemDTO,
} from '@prism/contracts';
import { api } from '../client';
import { queryKeys } from '../queryKeys';
import { usePaged } from './paged';

export function useStorage() {
  return useQuery({
    queryKey: queryKeys.storage(),
    queryFn: () => api<{ items: StorageLocationSummary[]; policy: StoragePolicyDTO | null }>('/api/v1/storage-locations'),
  });
}

export function useAgents(enabled = true) {
  return useQuery({ queryKey: queryKeys.agents(), queryFn: () => api<{ items: StorageAgentDTO[] }>('/api/v1/agents'), enabled, refetchInterval: 5000 });
}

export function useTransfers(assetId: string) {
  return usePaged<TransferDTO>({
    queryKey: queryKeys.transfers(assetId),
    url: `/api/v1/transfers?assetId=${assetId}`,
    refetchInterval: (items) => (items.some((t) => t.status === 'queued' || t.status === 'running') ? 2000 : false),
  });
}

/** Where an export can go: the exports tier or an export location. */
export function useExportDestinations(enabled = true) {
  return useQuery({ queryKey: queryKeys.exportDestinations(), queryFn: () => api<{ items: ExportDestinationDTO[] }>('/api/v1/export-destinations'), enabled });
}

/* ------------------------------------------------------------------ watch folders */

export function useWatchFolders(enabled = true) {
  return useQuery({
    queryKey: queryKeys.watchFolders(),
    queryFn: () => api<{ items: WatchFolderDTO[]; mountsRoot: string | null }>('/api/v1/watch-folders'),
    enabled,
    refetchInterval: 5000,
  });
}

export function useWatchFolderItems(id: string | null) {
  return useQuery({
    queryKey: queryKeys.watchFolderItems(id),
    queryFn: () => api<{ items: WatchFolderItemDTO[] }>(`/api/v1/watch-folders/${id}/items`),
    enabled: Boolean(id),
    refetchInterval: 4000,
  });
}

/* ------------------------------------------------------------------ transcoding */

export type TranscoderStatus =
  | { configured: false }
  | { configured: true; reachable: true; info: TranscoderInfo }
  | { configured: true; reachable: false; engine: string; error: string };
export function useTranscoders() {
  return useQuery({ queryKey: queryKeys.transcoders(), queryFn: () => api<TranscoderStatus>('/api/v1/transcoders'), staleTime: 30_000 });
}

export interface TranscodeProfileDTO { id: string; name: string; purpose: 'rendition' | 'proxy'; args: string; extension: string; hardware: boolean; updatedAt: string; inUse: number }
export interface TranscodeProfilesDTO {
  builtin: Array<{ id: string; name: string; extension: string; args: string; customised: boolean; hardware: boolean; editableArgs: boolean; defaultArgs: string | null; inUse: number }>;
  hiddenBuiltin: Array<{ id: string; name: string }>;
  builtinProxy: { name: string; args: string };
  items: TranscodeProfileDTO[];
  proxyProfileId: string | null;
  proxyHardware: boolean;
  /** 1.2.1: also decode on the hardware (with a hardware encoder). */
  proxyHardwareDecode?: boolean;
  /** 1.6.1: the built-in video proxy's bitrate cap (Mbit/s). */
  proxyBitrateCapMbps?: number;
}
export function useTranscodeProfiles() {
  return useQuery({ queryKey: queryKeys.transcodeProfiles(), queryFn: () => api<TranscodeProfilesDTO>('/api/v1/transcode-profiles') });
}

/** Send to edit (1.0.0-rc.6): the edit locations the dialog offers. */
export function useEditDestinations(enabled = true) {
  return useQuery({ queryKey: queryKeys.editDestinations(), queryFn: () => api<{ items: import('@prism/contracts').EditDestinationDTO[] }>('/api/v1/edit-destinations'), enabled });
}

/** Hand-offs, a sequence's or all; refreshed every 2 s while one is being made (events refresh it too). */
export function useHandoffs(sequenceId?: string, opts: { includeRemoved?: boolean } = {}) {
  const params = new URLSearchParams({ ...(sequenceId ? { sequenceId } : {}), ...(opts.includeRemoved ? { includeRemoved: 'true' } : {}) }).toString();
  return useQuery({
    // Fixes after rc.9 testing: hand-offs removed from the Import list only when asked for (the Exports list does).
    queryKey: opts.includeRemoved ? [...queryKeys.handoffs(sequenceId), 'removed'] : queryKeys.handoffs(sequenceId),
    queryFn: () => api<{ items: import('@prism/contracts').HandoffDTO[] }>(`/api/v1/handoffs${params ? `?${params}` : ''}`),
    refetchInterval: (q) => (q.state.data?.items.some((h) => h.status === 'queued' || h.status === 'running') ? 2000 : false),
  });
}
